/**
 * Webhook CRUD (FS-230) — dash data-layer client + pure helpers.
 *
 * Thin typed wrappers around `apiClient` for `/api/v1/webhook-crud`:
 *
 *   GET    /             list tenant webhooks
 *   POST   /             create
 *   GET    /:id          fetch one
 *   PATCH  /:id          update
 *   DELETE /:id          soft-delete
 *   POST   /:id/test     enqueue a synthetic test payload
 *
 * Pure helpers (`validateDraft`, `parseHeadersJson`, `urlHint`, status class,
 * query keys) live here so they can be unit-tested without React.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Constants — mirror server whitelist + reserved headers.
// ---------------------------------------------------------------------------

export const ALLOWED_EVENT_TYPES = [
	"conversion.created",
	"conversion.updated",
	"click.received",
	"payout.issued",
	"campaign.approved",
	"campaign.rejected",
	"affiliate.approved",
	"affiliate.rejected",
	"fraud.flagged",
	"*",
] as const;

export type WebhookEventType = (typeof ALLOWED_EVENT_TYPES)[number];

export const RESERVED_HEADER_NAMES = [
	"Authorization",
	"Content-Type",
	"Content-Length",
	"Host",
];

const RESERVED_SET = new Set(RESERVED_HEADER_NAMES.map((h) => h.toLowerCase()));

const NAME_RE = /^[a-z0-9_-]+$/;
const NAME_MIN = 2;
const NAME_MAX = 64;
const DESCRIPTION_MAX = 500;

// ---------------------------------------------------------------------------
// Server types
// ---------------------------------------------------------------------------

export interface WebhookView {
	id: string;
	name: string;
	url: string;
	event_types: string[];
	headers_json: Record<string, string> | null;
	active: boolean;
	description: string | null;
	created_at: number;
	updated_at: number;
	deleted_at: number | null;
}

// ---------------------------------------------------------------------------
// Draft + validation
// ---------------------------------------------------------------------------

export interface WebhookDraft {
	name: string;
	url: string;
	event_types: string[];
	active: boolean;
	description: string;
	headers_text: string;
}

export const EMPTY_WEBHOOK_DRAFT: WebhookDraft = {
	name: "",
	url: "",
	event_types: [],
	active: true,
	description: "",
	headers_text: "",
};

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface ValidatedWebhookDraft {
	name: string;
	url: string;
	event_types: string[];
	active: boolean;
	description: string | null;
	headers_json: Record<string, string> | null;
}

/** Parse `headers_text` (a JSON object, or empty) into a map or return a
 *  field-specific error. */
export function parseHeadersJson(
	raw: string,
): { ok: true; value: Record<string, string> | null } | { ok: false; reason: string } {
	const trimmed = raw.trim();
	if (trimmed.length === 0) return { ok: true, value: null };
	let parsed: unknown;
	try {
		parsed = JSON.parse(trimmed);
	} catch {
		return { ok: false, reason: "Headers must be valid JSON." };
	}
	if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
		return { ok: false, reason: "Headers must be a JSON object." };
	}
	const out: Record<string, string> = {};
	for (const [k, v] of Object.entries(parsed)) {
		if (typeof v !== "string") {
			return { ok: false, reason: `Header "${k}" must be a string.` };
		}
		if (RESERVED_SET.has(k.toLowerCase())) {
			return {
				ok: false,
				reason: `Reserved header "${k}" is not allowed.`,
			};
		}
		out[k] = v;
	}
	return { ok: true, value: Object.keys(out).length === 0 ? null : out };
}

/** Client-side validation mirroring the API. Returns normalized values. */
export const webhookDraftSchema = {
	safeParse(input: WebhookDraft): ParseResult<ValidatedWebhookDraft> {
		const errors: Record<string, string> = {};

		const name = (input.name ?? "").trim();
		if (name.length < NAME_MIN) {
			errors.name = `Name must be at least ${NAME_MIN} characters.`;
		} else if (name.length > NAME_MAX) {
			errors.name = `Name must be ${NAME_MAX} characters or fewer.`;
		} else if (!NAME_RE.test(name)) {
			errors.name = "Name must contain only a-z, 0-9, underscore, or hyphen.";
		}

		const url = (input.url ?? "").trim();
		if (!url.startsWith("https://")) {
			errors.url = "URL must start with https://";
		} else {
			try {
				// eslint-disable-next-line no-new
				new URL(url);
			} catch {
				errors.url = "URL is malformed.";
			}
		}

		const event_types = Array.from(new Set(input.event_types ?? []));
		if (event_types.length === 0) {
			errors.event_types = "Select at least one event type.";
		} else {
			for (const ev of event_types) {
				if (!(ALLOWED_EVENT_TYPES as readonly string[]).includes(ev)) {
					errors.event_types = `Unknown event type: ${ev}`;
					break;
				}
			}
		}

		const description = (input.description ?? "").trim();
		if (description.length > DESCRIPTION_MAX) {
			errors.description = `Description must be ${DESCRIPTION_MAX} characters or fewer.`;
		}

		const headersRes = parseHeadersJson(input.headers_text ?? "");
		let headers_json: Record<string, string> | null = null;
		if (!headersRes.ok) {
			errors.headers_text = headersRes.reason;
		} else {
			headers_json = headersRes.value;
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };

		return {
			success: true,
			data: {
				name,
				url,
				event_types,
				active: input.active,
				description: description.length === 0 ? null : description,
				headers_json,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function statusBadgeClass(active: boolean): string {
	return active
		? "bg-emerald-50 text-emerald-700 border-emerald-200"
		: "bg-zinc-50 text-zinc-600 border-zinc-200";
}

export function statusLabel(active: boolean): string {
	return active ? "Active" : "Paused";
}

export function formatTimestamp(seconds: number | null | undefined): string {
	if (seconds === null || seconds === undefined) return "\u2014";
	if (!Number.isFinite(seconds) || Number.isNaN(seconds)) return "\u2014";
	const d = new Date(seconds * 1000);
	if (Number.isNaN(d.getTime())) return "\u2014";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

/** Short hint returned as the user types a URL — guides them to https. */
export function urlHint(raw: string): { ok: boolean; message: string } {
	const trimmed = raw.trim();
	if (trimmed.length === 0) return { ok: false, message: "URL is required." };
	if (!trimmed.startsWith("https://")) {
		if (trimmed.startsWith("http://")) {
			return { ok: false, message: "Webhooks require https://, not http://." };
		}
		return { ok: false, message: "URL must start with https://" };
	}
	try {
		// eslint-disable-next-line no-new
		new URL(trimmed);
	} catch {
		return { ok: false, message: "URL is malformed." };
	}
	return { ok: true, message: "Looks good." };
}

/** Serialize a headers map back to pretty-printed JSON for the editor. */
export function stringifyHeaders(map: Record<string, string> | null): string {
	if (!map || Object.keys(map).length === 0) return "";
	return JSON.stringify(map, null, 2);
}

/** Build a draft from an existing webhook (used by the edit form). */
export function webhookToDraft(w: WebhookView): WebhookDraft {
	return {
		name: w.name,
		url: w.url,
		event_types: [...w.event_types],
		active: w.active,
		description: w.description ?? "",
		headers_text: stringifyHeaders(w.headers_json),
	};
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/webhook-crud";

export async function listWebhooks(): Promise<WebhookView[]> {
	return apiClient.get<WebhookView[]>(BASE);
}

export async function createWebhookRemote(
	draft: ValidatedWebhookDraft,
): Promise<WebhookView> {
	return apiClient.post<WebhookView>(BASE, {
		name: draft.name,
		url: draft.url,
		event_types: draft.event_types,
		active: draft.active,
		description: draft.description,
		headers_json: draft.headers_json,
	});
}

export async function updateWebhookRemote(
	id: string,
	draft: ValidatedWebhookDraft,
): Promise<WebhookView> {
	return apiClient.patch<WebhookView>(
		`${BASE}/${encodeURIComponent(id)}`,
		{
			name: draft.name,
			url: draft.url,
			event_types: draft.event_types,
			active: draft.active,
			description: draft.description,
			headers_json: draft.headers_json,
		},
	);
}

export async function toggleWebhookActive(
	id: string,
	active: boolean,
): Promise<WebhookView> {
	return apiClient.patch<WebhookView>(
		`${BASE}/${encodeURIComponent(id)}`,
		{ active },
	);
}

export async function deleteWebhookRemote(id: string): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export async function testWebhookRemote(
	id: string,
): Promise<{ queued: boolean; test_id: string }> {
	return apiClient.post<{ queued: boolean; test_id: string; webhook_id: string }>(
		`${BASE}/${encodeURIComponent(id)}/test`,
	);
}

// ---------------------------------------------------------------------------
// TanStack Query keys
// ---------------------------------------------------------------------------

export const webhookCrudQueryKeys = {
	all: ["webhook-crud"] as const,
	list: () => [...webhookCrudQueryKeys.all, "list"] as const,
	one: (id: string) => [...webhookCrudQueryKeys.all, "one", id] as const,
};
